import { useState, useEffect, useRef, useCallback } from 'react';
import { useInterval } from './useInterval';
import { supabase } from '../supabaseClient';

// Maximum size for localStorage cache (in characters, ~500KB)
const MAX_CACHE_SIZE = 500000;
const DEFAULT_FIRE_ALERT_DISTANCE_MILES = 15;
const MAX_FIRE_ALERT_DISTANCE_MILES = 15;

/**
 * Safely store data in localStorage with size management.
 * If storage would exceed limit, clears old cache first.
 */
const safeLocalStorageSet = (key, data) => {
  try {
    const stringified = JSON.stringify(data);

    // Check if this would exceed our limit
    if (stringified.length > MAX_CACHE_SIZE) {
      console.warn(`Data for ${key} exceeds max cache size (${stringified.length} chars), not caching`);
      return false;
    }

    // Try to set, if it fails due to quota, clear old wildfire caches
    try {
      localStorage.setItem(key, stringified);
      return true;
    } catch (e) {
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('localStorage quota exceeded, clearing wildfire caches');
        // Clear all wildfire-related caches
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && (k.includes('Wildfire') || k.includes('wildfire') || k.includes('FireOutlook'))) {
            keysToRemove.push(k);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));

        // Try again
        localStorage.setItem(key, stringified);
        return true;
      }
      throw e;
    }
  } catch (err) {
    console.error(`Failed to cache ${key}:`, err);
    return false;
  }
};

/**
 * Safely get data from localStorage with error handling.
 */
const safeLocalStorageGet = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (err) {
    console.error(`Failed to read ${key} from localStorage:`, err);
    return defaultValue;
  }
};

const fetchWildfireData = async (site, excludeRxBurns = true, maxFireDistance = DEFAULT_FIRE_ALERT_DISTANCE_MILES) => {
  const cappedFireDistance = Math.min(Math.max(maxFireDistance, 0), MAX_FIRE_ALERT_DISTANCE_MILES);
  const filterFires = (fires = []) => (excludeRxBurns
    ? fires.filter(fire => !fire.fire_name?.toLowerCase().includes('rx'))
    : fires
  ).filter(fire => fire.distance_miles != null && fire.distance_miles <= cappedFireDistance);
  const summarizeFires = (fires = []) => {
    const firesByDistance = {
      critical: fires.filter(f => f.distance_miles <= 5),
      warning: fires.filter(f => f.distance_miles > 5 && f.distance_miles <= 10),
      monitor: fires.filter(f => f.distance_miles > 10 && f.distance_miles <= 15)
    };

    return {
      total: fires.length,
      critical: firesByDistance.critical.length,
      warning: firesByDistance.warning.length,
      watch: 0,
      monitor: firesByDistance.monitor.length,
      nearest: fires[0] || null
    };
  };

  // Load cached data as fallback
  let wildfireData = filterFires(safeLocalStorageGet(`${site}_lastWildfireData`, []));
  let wildfireSummary = summarizeFires(wildfireData);
  let fireOutlook = safeLocalStorageGet(`${site}_lastFireOutlook`, []);
  let isStale = true;  // Assume stale until fresh data is fetched
  let fetchError = null;

  try {
    // Calculate cutoff time (24 hours ago) to filter stale fires
    const cutoffTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    // Fetch active wildfires from Supabase (only fires fetched in last 24h)
    const { data: fetchedFires, error } = await supabase
      .from('ivanhoe_wildfire_data')
      .select('*')
      .eq('site_name', site)
      .gte('fetched_at', cutoffTime)
      .order('distance_miles', { ascending: true });

    if (error) {
      console.error(`Error fetching wildfire data from Supabase: ${error.message}`);
      fetchError = error.message;
    } else if (Array.isArray(fetchedFires)) {
      isStale = false;  // Fresh data!

      wildfireData = filterFires(fetchedFires);
      safeLocalStorageSet(`${site}_lastWildfireData`, wildfireData);

      wildfireSummary = summarizeFires(wildfireData);
      safeLocalStorageSet(`${site}_lastWildfireSummary`, wildfireSummary);
    }

  } catch (err) {
    console.error(`Error fetching wildfire data for ${site}: ${err.message}`);
    fetchError = err.message;
  }

  return { wildfireData, wildfireSummary, fireOutlook, isStale, fetchError };
};

export const useWildfireData = (site, refreshInterval = 5) => {
  const [wildfireData, setWildfireData] = useState([]);
  const [wildfireSummary, setWildfireSummary] = useState({});
  const [fireOutlook, setFireOutlook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [excludeRxBurns, setExcludeRxBurns] = useState(true);
  const [isStale, setIsStale] = useState(false);  // Indicates data is from cache
  const [lastFetchTime, setLastFetchTime] = useState(null);  // Track when data was last fetched
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!site) {
      setError('Site is required');
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      // Fetch alert settings to get fire_exclude_rx_burns setting
      const { data: alertData } = await supabase
        .from('alert_thresholds')
        .select('fire_exclude_rx_burns, fire_alert_distance_miles')
        .eq('site_name', site)
        .single();

      if (requestId !== requestIdRef.current) {
        return;
      }

      const shouldExcludeRx = alertData?.fire_exclude_rx_burns ?? true;
      const maxFireDistance = Math.min(
        Math.max(alertData?.fire_alert_distance_miles ?? DEFAULT_FIRE_ALERT_DISTANCE_MILES, 0),
        MAX_FIRE_ALERT_DISTANCE_MILES
      );
      setExcludeRxBurns(shouldExcludeRx);

      const data = await fetchWildfireData(site, shouldExcludeRx, maxFireDistance);
      if (requestId !== requestIdRef.current) {
        return;
      }

      setWildfireData(data.wildfireData);
      setWildfireSummary(data.wildfireSummary);
      setFireOutlook(data.fireOutlook);
      setIsStale(data.isStale);

      if (!data.isStale) {
        setLastFetchTime(new Date());
      }

      // Set error if we got stale data due to fetch failure
      if (data.fetchError && data.isStale) {
        setError(`Using cached data. Fetch failed: ${data.fetchError}`);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError(`Failed to fetch wildfire data: ${err.message}`);
      setIsStale(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [site]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshInterval]);

  useInterval(() => {
    if (refreshInterval > 0) {
      fetchData();
    }
  }, refreshInterval > 0 ? refreshInterval * 60 * 1000 : null);

  // Helper function to get fire alert level based on distance
  const getFireAlertLevel = (fire) => {
    if (!fire || fire.distance_miles == null) return 'none';

    if (fire.distance_miles <= 5) return 'critical';
    if (fire.distance_miles <= 10) return 'warning';
    if (fire.distance_miles <= 15) return 'monitor';
    return 'none';
  };
  
  // Get fires grouped by alert level
  const getFiresByAlertLevel = () => {
    const grouped = {
      critical: [],
      warning: [],
      monitor: []
    };
    
    wildfireData.forEach(fire => {
      const level = getFireAlertLevel(fire);
      if (grouped[level]) {
        grouped[level].push(fire);
      }
    });
    
    return grouped;
  };
  
  // Get the highest risk fire outlook day
  const getHighestFireRisk = () => {
    if (!fireOutlook || fireOutlook.length === 0) return null;
    
    const riskLevels = {
      'extreme': 5,
      'critical': 4,
      'elevated': 3,
      'moderate': 2,
      'low': 1
    };
    
    let highest = null;
    fireOutlook.forEach(day => {
      if (!highest || (riskLevels[day.risk_category] || 0) > (riskLevels[highest.risk_category] || 0)) {
        highest = day;
      }
    });
    
    return highest;
  };

  // Function to refresh data when alert settings change
  const refreshWithNewSettings = async () => {
    await fetchData();
  };

  return {
    wildfireData,
    wildfireSummary,
    fireOutlook,
    loading,
    error,
    isStale,  // true if displaying cached data due to fetch failure
    lastFetchTime,  // timestamp of last successful fetch
    fetchData,
    refreshWithNewSettings,
    getFireAlertLevel,
    getFiresByAlertLevel,
    getHighestFireRisk,
    excludeRxBurns
  };
};
